'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { useCallback, useEffect, useState } from 'react'
import { fetchCampaignBySlug, fetchFeaturedCampaign, type PublicCampaign } from './api-client'

export interface PublicCampaignState {
  campaign: PublicCampaign | null
  loading: boolean
  /** True once the lookup settled and there is nothing to show. */
  unavailable: boolean
  /** Re-run the lookup — used after a claim to refresh the remaining count. */
  reload: () => void
}

/**
 * Loads the campaign a surface should display: the site-wide featured wave, or
 * one addressed by slug.
 *
 * "Not found" is a normal outcome, not an error: an absent or closed campaign
 * simply means the section does not render. A transport failure is treated the
 * same way — a marketing banner must never turn a flaky request into a broken
 * page.
 */
export function usePublicCampaign(slug?: string): PublicCampaignState {
  const [campaign, setCampaign] = useState<PublicCampaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [settled, setSettled] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    let active = true
    setLoading(true)

    const lookup = slug
      ? fetchCampaignBySlug(slug, { signal: controller.signal })
      : fetchFeaturedCampaign({ signal: controller.signal })

    lookup
      .then((result) => {
        if (!active) return
        setCampaign(result)
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, {
          source: 'features/promotion-campaign/use-public-campaign.ts',
        })
        if (!active) return
        setCampaign(null)
      })
      .finally(() => {
        if (!active) return
        setLoading(false)
        setSettled(true)
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [slug, reloadToken])

  return { campaign, loading, unavailable: settled && campaign === null, reload }
}
